import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Coins } from "lucide-react";
import { getCostSavingsReport } from "@/api/reports";
import { useCostUnit } from "@/hooks/useCostUnit";
import { formatCurrency, formatNumber } from "@/lib/format";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import type {
  CostSavingsGroupBy,
  CostSavingsRow,
  ReportParams,
} from "@/types/reports";

interface Props {
  params: ReportParams;
}

const SKELETON_ROWS = 5;
const DASH = "—";

/** Null-guards money before formatCurrency (Sprint 107 NaN lesson). */
function money(
  amount: number | null,
  currency: string,
  locale: string,
): string {
  if (amount == null) return DASH;
  return formatCurrency(amount, currency, locale);
}

function savingsClass(v: number | null): string {
  if (v == null || v === 0) return "text-gray-400";
  return v > 0
    ? "text-emerald-600 font-semibold"
    : "text-rose-600 font-semibold";
}

function savingsLabel(
  v: number | null,
  currency: string,
  locale: string,
): string {
  if (v == null) return DASH;
  const formatted = formatCurrency(Math.abs(v), currency, locale);
  if (v > 0) return `+${formatted}`;
  if (v < 0) return `-${formatted}`;
  return formatCurrency(0, currency, locale);
}

function groupName(
  row: CostSavingsRow,
  groupBy: CostSavingsGroupBy,
  t: (k: string) => string,
): string {
  if (row.group_name) return row.group_name;
  return groupBy === "depot"
    ? t("reports.costSavings.noDepot")
    : t("reports.costSavings.unassigned");
}

/**
 * Cost-savings report table (TASK-3737). Owns the driver/depot group-by toggle
 * in its card header; the query key includes group_by. No pagination, no
 * export, no column customization. Money cells are null-guarded; savings are
 * colored (positive emerald / negative rose) with a tfoot totals row.
 */
export function CostSavingsReportTable({ params }: Props) {
  const { t, i18n } = useTranslation();
  const currency = useCostUnit();
  const locale = i18n.language;
  const [groupBy, setGroupBy] = useState<CostSavingsGroupBy>("driver");

  const { data, isLoading } = useQuery({
    queryKey: ["reports", "cost-savings", params, groupBy],
    queryFn: () => getCostSavingsReport({ ...params, group_by: groupBy }),
  });

  const rows = data?.results ?? [];

  const totals = rows.reduce(
    (acc, r) => ({
      planned: acc.planned + (r.planned_cost ?? 0),
      actual: acc.actual + (r.actual_cost ?? 0),
      savings: acc.savings + (r.savings ?? 0),
      routes: acc.routes + r.routes_count,
    }),
    { planned: 0, actual: 0, savings: 0, routes: 0 },
  );

  const TOGGLES: { key: CostSavingsGroupBy; label: string }[] = [
    { key: "driver", label: t("reports.costSavings.byDriver") },
    { key: "depot", label: t("reports.costSavings.byDepot") },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      {/* Card header: group-by toggle */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("reports.costSavings.groupBy")}
        </span>
        <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1">
          {TOGGLES.map((tg) => (
            <button
              key={tg.key}
              type="button"
              onClick={() => setGroupBy(tg.key)}
              className={cn(
                "rounded-lg px-3 py-1 text-xs font-semibold transition-all",
                groupBy === tg.key
                  ? "bg-brand-primary text-white shadow-sm"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
              )}
            >
              {tg.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60">
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-gray-400">
                {groupBy === "depot"
                  ? t("reports.costSavings.depot")
                  : t("reports.costSavings.driver")}
              </th>
              <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("reports.costSavings.routes")}
              </th>
              <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("reports.costSavings.plannedCost")}
              </th>
              <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("reports.costSavings.actualCost")}
              </th>
              <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-widest text-gray-400">
                {t("reports.costSavings.savings")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50 last:border-0">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className="px-5 py-4">
                        <Skeleton className="h-4 w-20" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row) => (
                  <tr
                    key={`${groupBy}-${row.group_key ?? "none"}`}
                    className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50"
                  >
                    <td className="px-5 py-4 font-semibold text-gray-900">
                      {groupName(row, groupBy, t)}
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums text-gray-500">
                      {formatNumber(row.routes_count, locale)}
                    </td>
                    <td className="px-5 py-4 text-right font-medium tabular-nums text-gray-700">
                      {money(row.planned_cost, currency, locale)}
                    </td>
                    <td className="px-5 py-4 text-right font-medium tabular-nums text-gray-700">
                      {money(row.actual_cost, currency, locale)}
                    </td>
                    <td
                      className={cn(
                        "px-5 py-4 text-right tabular-nums",
                        savingsClass(row.savings),
                      )}
                    >
                      {savingsLabel(row.savings, currency, locale)}
                    </td>
                  </tr>
                ))}
          </tbody>
          {!isLoading && rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-gray-100 bg-gray-50/60 font-semibold">
                <td className="px-5 py-3.5 text-gray-700">
                  {t("reports.costSavings.total")}
                </td>
                <td className="px-5 py-3.5 text-right tabular-nums text-gray-700">
                  {formatNumber(totals.routes, locale)}
                </td>
                <td className="px-5 py-3.5 text-right tabular-nums text-gray-700">
                  {formatCurrency(totals.planned, currency, locale)}
                </td>
                <td className="px-5 py-3.5 text-right tabular-nums text-gray-700">
                  {formatCurrency(totals.actual, currency, locale)}
                </td>
                <td
                  className={cn(
                    "px-5 py-3.5 text-right tabular-nums",
                    savingsClass(totals.savings),
                  )}
                >
                  {savingsLabel(totals.savings, currency, locale)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {!isLoading && rows.length === 0 && (
        <EmptyState icon={Coins} title={t("reports.costSavings.noData")} />
      )}
    </div>
  );
}
